import React from 'react';
import Breadcrumb from '../../components/breadcrumb';
import UQMenu from '../../components/menu';
import UQSpacer from '../../components/spacer';
import UQCode from '../../components/code';
import UQDataGrid, { UQDataGridColumn } from '../../components/data-grid';
import { BREADCRUMB_ROOT } from '../../common/config';

const MenuDemoPage = () => {
  const menuItems = [
    {
      text: 'Home',
      icon: 'home-01',
      url: '/',
    },
    {
      text: 'Actions',
      icon: 'loader-01',
      subItems: [
        { text: 'Button', url: '/components/button' },
        { text: 'Dropdown', url: '/components/dropdown' },
        { text: 'Modal', url: '/components/modal' },
      ],
    },
    {
      text: 'Data Display',
      icon: 'card-01',
      subItems: [
        { text: 'Accordion', url: '/components/accordion' },
        { text: 'Card', url: '/components/card' },
        { text: 'Card Big Text', url: '/components/card-big-text' },
      ],
    },
    {
      text: 'Disabled Item',
      icon: 'edit-03',
      disabled: true,
    },
  ];

  const handleItemClick = (item: { text: string; url?: string }) => {
    console.log('Clicked item:', item);
  };

  const attributes = [
    {
      attribute: 'items',
      type: 'Array<{ text: string; icon?: string; url?: string; subItems?: Array<object>; disabled?: boolean }>',
      default: '[]',
      description: 'The menu items to display.',
    },
    {
      attribute: 'onItemClick',
      type: '(item: { text: string; url?: string }) => void',
      default: 'undefined',
      description: 'Callback function triggered when a menu item is clicked.',
    },
    {
      attribute: 'className',
      type: 'string',
      default: "''",
      description: 'Additional class names for custom styling.',
    },
  ];

  const attributeColumns: UQDataGridColumn<typeof attributes[0]>[] = [
    {
      columnId: 'attribute',
      renderHeaderCell: () => 'Attribute',
      renderCell: (item) => item.attribute,
    },
    {
      columnId: 'type',
      renderHeaderCell: () => 'Type',
      renderCell: (item) => <code>{item.type}</code>,
    },
    {
      columnId: 'default',
      renderHeaderCell: () => 'Default',
      renderCell: (item) => <code>{item.default}</code>,
    },
    {
      columnId: 'description',
      renderHeaderCell: () => 'Description',
      renderCell: (item) => item.description,
    },
  ];

  const menuCodeSnippet = `
import React from 'react';
import { UQMenu } from '@uniqconsulting/ui';

const menuItems = [
  {
    text: 'Home',
    icon: 'home-01',
    url: '/',
  },
  {
    text: 'Actions',
    icon: 'loader-01',
    subItems: [
      { text: 'Button', url: '/components/button' },
      { text: 'Dropdown', url: '/components/dropdown' },
      { text: 'Modal', url: '/components/modal' },
    ],
  },
  {
    text: 'Data Display',
    icon: 'card-01',
    subItems: [
      { text: 'Accordion', url: '/components/accordion' },
      { text: 'Card', url: '/components/card' },
      { text: 'Card Big Text', url: '/components/card-big-text' },
    ],
  },
  {
    text: 'Disabled Item',
    icon: 'edit-03',
    disabled: true,
  },
];

<UQMenu items={menuItems} onItemClick={(item) => console.log('Clicked item:', item)} />;
`;

  return (
    <>
      <Breadcrumb crumbs={[BREADCRUMB_ROOT, 'Menu']} />
      <h1>UQMenu Demo</h1>
      <UQSpacer size="m" />

      {/* Description */}
      <h2>Description</h2>
      <p>
        The <code>UQMenu</code> component is a flexible menu system that supports hierarchical items, icons, and disabled states. It is designed to be lightweight and customizable.
      </p>
      <UQSpacer size="m" />

      {/* Attributes Table */}
      <h2>Attributes</h2>
      <UQSpacer size="s" />
      <UQDataGrid
        items={attributes}
        columns={attributeColumns}
        sortable={false}
        selectionMode="none"
      />
      <UQSpacer size="m" />

      {/* Live Demo */}
      <h2>Live Demo</h2>
      <UQSpacer size="s" />
      <UQMenu items={menuItems} onItemClick={handleItemClick} />
      <UQSpacer size="m" />

      {/* Code Example */}
      <h2>Code Example</h2>
      <p>
        Below is an example of how to use the <code>UQMenu</code> component with hierarchical items and icons.
      </p>
      <UQSpacer size="s" />
      <UQCode code={menuCodeSnippet} enableCopy />
    </>
  );
};

export default MenuDemoPage;